import { useMemo } from "react";

/**
 * JobProgress — segmented, hoverable pipeline bar.
 *
 * Reads `stage_counts` off the job doc and renders one coloured band per
 * stage, sized proportionally to the candidate count in that stage. Hover
 * a band → tooltip with the stage name + count.
 *
 * Colour language:
 *   - success stages (offer/hired/joined/accepted) → emerald shades
 *   - mid-funnel (screening/interview/assessment/onsite) → blue / indigo
 *   - top-of-funnel (applied/new/sourced) → slate
 *   - negative stages (rejected/withdrawn/declined) → rose (grouped last)
 *
 * The bar is `h-4` (double the previous `h-2`) so bands are easy to hit
 * on desktop and readable on retina.
 */
const REJECTED_STAGES = new Set(["Rejected", "Disqualified", "Withdrawn", "Declined"]);
const HIRED_STAGES = new Set(["Hired", "Offer Accepted", "Joined", "Placed"]);
const APPLIED_STAGES = new Set(["Applied", "New", "Sourced"]);

function daysBetween(a, b) {
    const ms = b - a;
    return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
}

function parseDate(s) {
    if (!s) return null;
    const d = new Date(s.length === 10 ? s + "T00:00:00" : s);
    return Number.isNaN(d.getTime()) ? null : d;
}

/** Categorise a stage into one of {applied, in_progress, hired, rejected}. */
function stageCategory(stage) {
    if (HIRED_STAGES.has(stage)) return "hired";
    if (REJECTED_STAGES.has(stage)) return "rejected";
    if (APPLIED_STAGES.has(stage)) return "applied";
    return "in_progress";
}

/** Pick a Tailwind class per stage. Green shades for success (darker →
 *  closer to hire), amber for offer, blue for mid-funnel, slate for
 *  top-of-funnel, rose for negative. */
function stageColor(stage) {
    const s = (stage || "").toLowerCase();
    if (s === "hired" || s === "joined" || s === "placed") return "bg-emerald-600";
    if (s === "offer accepted") return "bg-emerald-500";
    if (s.includes("offer")) return "bg-amber-500";
    if (s.includes("onsite") || s.includes("final")) return "bg-indigo-500";
    if (s.includes("interview") || s.includes("assessment") || s.includes("technical")) return "bg-ikb";
    if (s.includes("screen") || s.includes("phone") || s.includes("recruiter")) return "bg-blue-400";
    if (s === "applied" || s === "new" || s === "sourced") return "bg-slate-400";
    if (REJECTED_STAGES.has(stage)) return "bg-rose-400/70";
    return "bg-slate-300";
}

export function summarizeProgress(job) {
    const sc = job.stage_counts || {};
    const total = Object.values(sc).reduce((a, b) => a + b, 0);
    let applied = 0, inProgress = 0, hired = 0, rejected = 0;
    for (const [stage, n] of Object.entries(sc)) {
        const cat = stageCategory(stage);
        if (cat === "hired") hired += n;
        else if (cat === "rejected") rejected += n;
        else if (cat === "applied") applied += n;
        else inProgress += n;
    }
    const openings = job.openings || 1;
    const hiredPct = Math.min(100, Math.round((hired / openings) * 100));
    const created = parseDate(job.created_at);
    const due = parseDate(job.due_date);
    const now = new Date();
    const daysOpen = created ? daysBetween(created, now) : null;
    let daysToDue = null;
    if (due) {
        const diff = daysBetween(now, due);
        const past = due < now;
        daysToDue = past ? -daysBetween(due, now) : diff;
    }
    return { total, applied, inProgress, hired, rejected, openings, hiredPct, daysOpen, daysToDue };
}

/** Ordered list of stages for a job, sorted so success comes last (right
 *  side of the bar) — mirrors a funnel reading left-to-right = applied → hired. */
function orderStages(stageCounts) {
    const entries = Object.entries(stageCounts || {}).filter(([, n]) => n > 0);
    const rank = (stage) => {
        const c = stageCategory(stage);
        if (c === "applied") return 0;
        if (c === "in_progress") return 1;
        if (c === "hired") return 2;
        return 3; // rejected sinks to the end
    };
    entries.sort((a, b) => rank(a[0]) - rank(b[0]));
    return entries;
}

export default function JobProgress({ job }) {
    const s = useMemo(() => summarizeProgress(job), [job]);
    const bands = useMemo(() => orderStages(job.stage_counts || {}), [job.stage_counts]);
    const total = Math.max(1, s.total);
    const pct = (n) => `${(n / total) * 100}%`;

    const ringTone = s.hiredPct >= 100
        ? "text-emerald-700"
        : (s.daysToDue !== null && s.daysToDue < 0)
            ? "text-rose-700"
            : (s.daysToDue !== null && s.daysToDue <= 7)
                ? "text-amber-700"
                : "text-foreground/80";

    return (
        <div className="relative group inline-flex flex-col items-stretch w-full" data-testid={`job-progress-${job.id}`}>
            <div className={`flex items-center justify-between text-[10px] font-mono uppercase tracking-widest mb-1 ${ringTone}`}>
                <span data-testid={`job-progress-pct-${job.id}`}>{s.hired}/{s.openings} hired</span>
                <span className="text-foreground/60">{s.total} app{s.total === 1 ? "" : "s"}</span>
            </div>
            {/* Thicker (h-4) segmented bar. Each band is one pipeline stage
             *  and carries a native tooltip + a data-testid for automated tests. */}
            <div className="h-4 w-full bg-secondary border border-border flex overflow-hidden" data-testid={`job-progress-bar-${job.id}`}>
                {bands.length === 0 ? (
                    <div className="w-full h-full bg-secondary" />
                ) : bands.map(([stage, n]) => (
                    <div
                        key={stage}
                        className={`h-full ${stageColor(stage)} border-r border-white/40 last:border-r-0 relative group/band cursor-help`}
                        style={{ width: pct(n) }}
                        data-testid={`job-progress-band-${job.id}-${stage.toLowerCase().replace(/\s+/g, "-")}`}
                        title={`${stage}: ${n}`}
                    >
                        {/* Rich hover tooltip that sits above the band (only
                         *  visible when hovering that specific band). */}
                        <div className="absolute z-40 bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover/band:block pointer-events-none whitespace-nowrap bg-foreground text-background text-[10px] font-mono uppercase tracking-widest px-2 py-1 shadow-lg">
                            <span className="font-bold">{n}</span> in <span className="opacity-80">{stage}</span>
                        </div>
                    </div>
                ))}
            </div>
            {/* Bar-wide summary tooltip (only appears if the row is hovered
             *  but no specific band — keeps the old drill-down available). */}
            <div
                className="absolute z-30 left-1/2 -translate-x-1/2 top-full mt-2 hidden group-hover:block group-hover/band:hidden pointer-events-none w-64 bg-white border border-ikb shadow-xl p-3 text-xs"
                role="tooltip"
                data-testid={`job-progress-tooltip-${job.id}`}
            >
                <div className="font-display font-bold text-sm mb-1.5 leading-tight">{job.title}</div>
                <div className="grid grid-cols-2 gap-y-1 font-mono text-[11px]">
                    <span className="text-foreground/70">Openings</span><span className="text-right">{s.openings}</span>
                    <span className="text-foreground/70">Hired</span><span className={`text-right ${s.hired >= s.openings ? "text-emerald-700 font-bold" : ""}`}>{s.hired}</span>
                    <span className="text-foreground/70">In progress</span><span className="text-right text-ikb">{s.inProgress}</span>
                    <span className="text-foreground/70">Applied</span><span className="text-right">{s.applied}</span>
                    <span className="text-foreground/70">Rejected</span><span className="text-right text-rose-700">{s.rejected}</span>
                    <span className="text-foreground/70">Total applicants</span><span className="text-right">{s.total}</span>
                    <span className="text-foreground/70 mt-1 pt-1 border-t border-border col-span-2"></span>
                    {s.daysOpen !== null && (<>
                        <span className="text-foreground/70">Open since</span><span className="text-right">{s.daysOpen}d</span>
                    </>)}
                    {s.daysToDue !== null && (<>
                        <span className="text-foreground/70">Due</span>
                        <span className={`text-right ${s.daysToDue < 0 ? "text-rose-700 font-bold" : s.daysToDue <= 7 ? "text-amber-700" : ""}`}>
                            {s.daysToDue < 0 ? `${-s.daysToDue}d overdue` : `in ${s.daysToDue}d`}
                        </span>
                    </>)}
                </div>
                {bands.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-border">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 mb-1">Stage breakdown</div>
                        <ul className="space-y-0.5">
                            {bands.map(([stage, n]) => (
                                <li key={stage} className="flex items-center justify-between text-[11px] font-mono">
                                    <span className="inline-flex items-center gap-1.5 truncate">
                                        <span className={`inline-block w-2 h-2 ${stageColor(stage)}`} />
                                        <span className="truncate">{stage}</span>
                                    </span>
                                    <span>{n}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}
